import { useMemo, type ChangeEvent } from 'react';
import { Box, Text } from '@rsos/components/capstone/base';
import Select from '@rsos/components/capstone/base/Select';
import { useFlag } from '@rsos/flags';
import type { SetFieldValue, SpatialObject } from '../../types';
import { Label } from '../AirAmbulanceFormStandalone.styles';
import { useLandingZonesLayer } from '../hooks';

interface LandingZoneSelectProps {
  setFieldValue: SetFieldValue;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleLandingZoneSelect: (
    selectedOption: SpatialObject | null,
    setFieldValue: SetFieldValue,
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void,
  ) => void;
  selectedLandingZone?: SpatialObject | null;
}

const LandingZoneSelect = ({
  setFieldValue,
  handleChange,
  handleLandingZoneSelect,
  selectedLandingZone,
}: LandingZoneSelectProps): JSX.Element | null => {
  const isGmrLandingZoneEnabled = useFlag('gmr_landing_zone');
  const { spatialObjects } = useLandingZonesLayer();

  const spatialObjectOptions = useMemo(() => {
    if (!spatialObjects || !Array.isArray(spatialObjects)) return [];
    return spatialObjects as SpatialObject[];
  }, [spatialObjects]);

  const selectedOption = useMemo(() => {
    if (!selectedLandingZone) return null;
    return (
      spatialObjectOptions.find(obj => obj?.id === selectedLandingZone?.id) ||
      null
    );
  }, [spatialObjectOptions, selectedLandingZone]);

  const handleSpatialObjectSelect = (
    selectedOption: SpatialObject | null,
  ): void => {
    handleLandingZoneSelect(selectedOption, setFieldValue, handleChange);
  };

  // Only render if the feature flag is enabled and there are spatial objects
  if (!isGmrLandingZoneEnabled || !spatialObjectOptions.length) {
    return null;
  }

  return (
    <Box pb={3}>
      <Label>
        <Text size="small" color="primaryText">
          Landing Zone
        </Text>
      </Label>
      <Select
        options={spatialObjectOptions}
        getOptionLabel={(option: SpatialObject) => option?.name || ''}
        getOptionValue={(option: SpatialObject) => String(option?.id ?? '')}
        value={selectedOption}
        placeholder="Select a landing zone..."
        onChange={handleSpatialObjectSelect}
        isClearable={true}
        isSearchable={true}
      />
    </Box>
  );
};

export default LandingZoneSelect;
